import { useApp } from '@/contexts';

import { useEffect, useMemo, useState } from 'react';

import { useHeaderBreadcrumb } from '@components/layout';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { PageContainer, PageHeading } from '@/components/layout';
import type { Environment } from '@/types';
import { logger } from '@/utils/logger';

import {
  useCreateEnvironmentMutation,
  useGetCredentialsQuery,
  useGetEnvironmentsQuery,
} from '../api/environmentsApi';
import { EnvironmentCard } from '../components/EnvironmentCard';
import { EnvironmentDetailView } from '../components/EnvironmentDetailView';

type CreateEnvironmentFormData = {
  name: string;
  isProd: boolean;
};

/**
 * Environments Page - Manage deployment environments and connector credentials
 * Matches dataProtect environment management
 */
export const EnvironmentsPage = () => {
  const { appId, tenantId } = useApp();
  const { envId, '*': restPath } = useParams<{
    envId?: string;
    '*'?: string;
  }>();

  // Parse additional route patterns from wildcard path:
  // - "connections/:connectorName/connectionKeys/:connectionKey" (edit existing)
  // - "add-source" (show source selector)
  // - "add-destination" (show destination selector)
  // - "add-source/:providerType" (add new source)
  // - "add-destination/:providerType" (add new destination)
  // - "wizard/step-:stepNum" (wizard mode)
  const connectionMatch = restPath?.match(/^connections\/([^/]+)\/connectionKeys\/([^/]+)$/);
  const connectorName = connectionMatch?.[1];
  const connectionKey = connectionMatch?.[2];

  const addSourceMatch = restPath?.match(/^add-source(?:\/([^/]+))?$/);
  const addDestMatch = restPath?.match(/^add-destination(?:\/([^/]+))?$/);
  const wizardMatch = restPath?.match(/^wizard\/step-(\d+)(?:\/([^/]+))?$/);

  const isAddingSource = !!addSourceMatch;
  const isAddingDest = !!addDestMatch;
  const addingProviderType = addSourceMatch?.[1] || addDestMatch?.[1] || wizardMatch?.[2];
  const wizardStepNum = wizardMatch?.[1] ? parseInt(wizardMatch[1], 10) : null;
  const navigate = useNavigate();
  const { setContextLabel } = useHeaderBreadcrumb();
  const { showSnackbar } = useSnackbar();
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    data: environments,
    isLoading,
    error,
  } = useGetEnvironmentsQuery({ tenantId, appId }, { skip: !tenantId || !appId });

  const { data: credentials, isLoading: isLoadingCredentials } = useGetCredentialsQuery(
    {
      tenantId,
      appId,
      envName: selectedEnv?.name!,
    },
    { skip: !selectedEnv || !tenantId || !appId }
  );

  const [createEnvironment, { isLoading: isCreating }] = useCreateEnvironmentMutation();

  /**
   * Dynamic validation schema with duplicate name check
   * Validates as user types (real-time validation)
   * Memoized to avoid recreating schema on every render
   */
  const createEnvironmentSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, 'Environment name is required')
          .regex(/^[a-zA-Z0-9_\s-]+$/, 'Environment name must not contain special characters')
          .refine(
            (name) => {
              const trimmedName = name.trim();
              const isDuplicate = environments?.some(
                (env) => env.name.toLowerCase() === trimmedName.toLowerCase()
              );
              return !isDuplicate;
            },
            {
              message: 'Environment name already exists. Please choose a different name.',
            }
          ),
        isProd: z.boolean(),
      }),
    [environments]
  );

  // React Hook Form for create environment dialog with validation
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<CreateEnvironmentFormData>({
    resolver: zodResolver(createEnvironmentSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      isProd: false,
    },
  });

  // Sync URL with selected environment
  useEffect(() => {
    if (envId && environments) {
      // Use String() comparison because useParams returns string but API may return number
      const env = environments.find((e) => String(e.id) === envId);
      if (env && env !== selectedEnv) {
        setSelectedEnv(env);
      }
    } else if (!envId && selectedEnv) {
      setSelectedEnv(null);
    }
  }, [envId, environments, selectedEnv]);

  // Show environment name in the header breadcrumb (TENANT ➤ APP ➤ ENV_NAME)
  useEffect(() => {
    if (selectedEnv) {
      setContextLabel(selectedEnv.name);
    } else {
      setContextLabel(null);
    }
    return () => setContextLabel(null);
  }, [selectedEnv, setContextLabel]);

  const handleCreateEnvironment = async (data: CreateEnvironmentFormData) => {
    if (!tenantId || !appId) return;

    const trimmedName = data.name.trim();

    try {
      await createEnvironment({
        tenantId,
        appId,
        data: {
          name: trimmedName,
          isProd: data.isProd,
        },
      }).unwrap();

      showSnackbar('Environment created successfully', 'success');
      setCreateDialogOpen(false);
      reset();
    } catch (err: unknown) {
      logger.error('Failed to create environment:', err);

      // Extract user-friendly error message from RTK Query error
      let errorMessage = 'Failed to create environment';

      // Handle RTK Query error format
      if (err && typeof err === 'object' && 'data' in err) {
        const errData = (err as { data: unknown }).data;

        if (typeof errData === 'string') {
          errorMessage = errData;
        } else if (errData && typeof errData === 'object') {
          if ('message' in errData && typeof errData.message === 'string') {
            errorMessage = errData.message;
          } else if ('error' in errData && typeof errData.error === 'string') {
            errorMessage = errData.error;
          }
        }
      } else if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = String((err as { message: unknown }).message);
      }

      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEnvClick = (env: Environment) => {
    setSelectedEnv(env);
    // Use id for URL routing (matches legacy dataProtect)
    navigate(`/a/${appId}/env/${env.id}`);
  };

  const handleBackToList = () => {
    setSelectedEnv(null);
    navigate(`/a/${appId}/env`);
  };

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">Failed to load environments.</Alert>
      </PageContainer>
    );
  }

  // If envId is in the URL, we are in detail mode - never show the list
  if (envId) {
    // Show detail view once environment is resolved and credentials are initially loaded.
    // Don't gate on isFetchingCredentials — stale cached data keeps the form mounted
    // during background refetches (e.g. after saving credentials), which preserves
    // local component state like connection-test error banners.
    if (selectedEnv && credentials && !isLoadingCredentials) {
      return (
        <PageContainer>
          <EnvironmentDetailView
            environment={selectedEnv}
            tenantId={tenantId}
            credentials={credentials}
            onBack={handleBackToList}
            initialConnectorName={connectorName}
            initialConnectionKey={connectionKey}
            initialAddSource={isAddingSource}
            initialAddDest={isAddingDest}
            initialProviderType={addingProviderType}
            initialWizardStep={wizardStepNum}
          />
        </PageContainer>
      );
    }

    // If environments loaded but no match found for envId, redirect to list
    if (environments && !environments.find((e) => String(e.id) === envId)) {
      return (
        <PageContainer>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Environment not found. Showing all environments.
          </Alert>
        </PageContainer>
      );
    }

    // Still loading (app, environments, selectedEnv resolution, or credentials)
    return (
      <PageContainer>
        <Skeleton variant="rectangular" height={400} />
      </PageContainer>
    );
  }

  // Sort environments alphabetically by name (as in legacy dataProtect)
  const sortedEnvs = [...(environments || [])].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <PageContainer>
      <PageHeading
        title="Manage Environments"
        subtitle="Configure your deployment environments and connector credentials."
        action={
          <Button variant="contained" size="small" onClick={() => setCreateDialogOpen(true)}>
            New Environment
          </Button>
        }
      />

      {/* Environments list */}
      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={60} />
          ))}
        </Box>
      )}
      {!isLoading && sortedEnvs.length === 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '13px' }}>
            No Environments found. Please create a new Environment.
          </Typography>
        </Box>
      )}
      {!isLoading && sortedEnvs.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {sortedEnvs.map((env) => (
            <EnvironmentCard
              key={env.id}
              environment={env}
              onClick={() => handleEnvClick(env)}
              tenantId={tenantId}
            />
          ))}
        </Box>
      )}

      {/* Create Environment Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Environment</DialogTitle>
        <form onSubmit={handleSubmit(handleCreateEnvironment)}>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Environment Name"
                    fullWidth
                    autoFocus
                    placeholder="e.g., Development, Staging, Production"
                    error={!!errors.name}
                    helperText={
                      errors.name?.message || 'Use letters, numbers, underscores, and hyphens only'
                    }
                  />
                )}
              />
              <Controller
                name="isProd"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="This is a production environment"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setCreateDialogOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!isValid || isCreating}>
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </PageContainer>
  );
};
